import "@mantine/core/styles.css"
import "@civala/shared-ui/styles/pitch-global.css"
import "./nexus.css"

import { createQueryClient } from "@civala/shared-ui/query"
import { pitchAppTheme } from "@civala/shared-ui/themes/pitch"
import { MantineProvider } from "@mantine/core"
import { QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { routeTree } from "./routeTree.gen"

// A lazily-imported route/chunk that 404s is almost always a stale index left in a tab that
// was open across a deploy (chunk hashes changed). Vite fires `vite:preloadError` for it —
// reload once to pull the fresh index + chunks. Time-guarded so a chunk that is genuinely gone
// can't spin an infinite reload loop. The window spans the CDN-propagation gap right after a
// deploy (fresh index can out-run its chunks by up to ~30s), so one reload per 30s covers it.
window.addEventListener("vite:preloadError", () => {
  const RELOAD_KEY = "nexus.chunkReloadAt"
  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
  if (Date.now() - last > 30_000) {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
    window.location.reload()
  }
})

const queryClient = createQueryClient({ app: "nexus" })

const router = createRouter({
  routeTree,
  defaultViewTransition: true,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={pitchAppTheme} defaultColorScheme="light">
        <RouterProvider router={router} />
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
)
